import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import ApiService from "../../services/api-service";
import notifier from "../../components/Notifier";
import { getUserId, hasProTier, copyShareUrl } from "../../constants/function";
import SubscriptionPopup from "../../components/SubscriptionPopup";
import "./PowerPlays.scss";

export function Waveform() {
  const waveRef = useRef(null);

  useEffect(() => {
    const el = waveRef.current;
    if (!el || el.children.length > 0) return;
    for (let i = 0; i < 50; i++) {
      const bar = document.createElement("div");
      bar.className = "bar";
      bar.style.height = (15 + Math.random() * 55) + "px";
      bar.style.animationDelay = (Math.random() * 1.4).toFixed(2) + "s";
      bar.style.animationDuration = (1 + Math.random() * 0.8).toFixed(2) + "s";
      el.appendChild(bar);
    }
  }, []);

  return <div className="pp-waveform" ref={waveRef} />;
}

export default function PowerPlays() {
  const [powerPlays, setPowerPlays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedPowerPlay, setSelectedPowerPlay] = useState(null);
  const [applicationText, setApplicationText] = useState("");
  const [selectedSong, setSelectedSong] = useState(null);
  const [userSongs, setUserSongs] = useState([]);
  const [loadingSongs, setLoadingSongs] = useState(false);
  const [userApplications, setUserApplications] = useState(new Map());
  const [subscriptionPopupOpen, setSubscriptionPopupOpen] = useState(false);
  const [isProUser, setIsProUser] = useState(hasProTier());
  const [isEditing, setIsEditing] = useState(false);

  // Fetch fresh user profile to get accurate Pro status
  useEffect(() => {
    const refreshProStatus = async () => {
      try {
        const response = await ApiService.USER_PROFILE();
        if (response.result) {
          const tier = response.result.premiumTier || response.result.subscriptionTier;
          setIsProUser(tier === 'pro');
        }
      } catch (error) {
        // Fall back to cached value
      }
    };
    if (getUserId()) refreshProStatus();
  }, []);

  const fetchUserApplications = async () => {
    try {
      const userId = getUserId();
      if (!userId) return;
      const response = await ApiService.get('/public/powerplays/my-applications');
      if (response.result) {
        const applications = Array.isArray(response.result) ? response.result : [response.result];
        const appMap = new Map();
        applications.forEach(app => appMap.set(app.powerPlayId, app));
        setUserApplications(appMap);
      }
    } catch (error) {
      console.error("Error fetching user applications:", error);
    }
  };

  const fetchPowerPlays = async () => {
    setLoading(true);
    try {
      const response = await ApiService.GET_POWERPLAYS();
      if (response.result) {
        const powerPlaysArray = Array.isArray(response.result) ? response.result : [response.result];
        setPowerPlays(powerPlaysArray);
      }
    } catch (error) {
      console.error("Error fetching PowerPlays:", error);
      notifier.error("Failed to load PowerPlays");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPowerPlays();
    fetchUserApplications();
  }, []);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchUserSongs = async () => {
    setLoadingSongs(true);
    try {
      const userId = getUserId();
      if (!userId) {
        notifier.error("Please log in to select a song");
        setLoadingSongs(false);
        return;
      }
      const response = await ApiService.MY_MUSIC(`assetId=${userId}&type=user`);
      if (response.result) {
        setUserSongs(Array.isArray(response.result) ? response.result : [response.result]);
      }
    } catch (error) {
      console.error("Error fetching user songs:", error);
      notifier.error("Failed to load your songs");
    } finally {
      setLoadingSongs(false);
    }
  };

  const handleApplyClick = useCallback((powerPlay) => {
    setSelectedPowerPlay(powerPlay);
    const existingApp = userApplications.get(powerPlay._id);
    if (existingApp) {
      setIsEditing(true);
      setApplicationText(existingApp.applicationText || "");
      setSelectedSong(existingApp.songId || null);
    } else {
      setIsEditing(false);
      setApplicationText("");
      setSelectedSong(null);
    }
    setApplyDialogOpen(true);
    if (powerPlay.requiresSongUpload) {
      fetchUserSongs();
    }
  }, [userApplications]);

  const handleCloseApplyDialog = () => {
    setApplyDialogOpen(false);
    setSelectedPowerPlay(null);
    setApplicationText("");
    setSelectedSong(null);
    setUserSongs([]);
    setIsEditing(false);
  };

  const handleSubmitApplication = async () => {
    if (!selectedPowerPlay || !applicationText.trim()) {
      notifier.error("Please provide application text");
      return;
    }
    if (selectedPowerPlay.requiresSongUpload && !selectedSong) {
      notifier.error("Please select a song");
      return;
    }
    try {
      const response = await ApiService.post(
        `/public/powerplays/${selectedPowerPlay._id}/apply`,
        {
          applicationText: applicationText.trim(),
          songId: selectedSong || null,
        }
      );
      if (response.ok) {
        notifier.success(isEditing ? "Application updated!" : "Application submitted successfully!");
        handleCloseApplyDialog();
        fetchUserApplications();
        fetchPowerPlays();
      } else {
        notifier.error(response.message || response.error || "Failed to submit application");
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      notifier.error(error.message || "Failed to submit application");
    }
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diff = end - now;
    if (diff < 0) return "Past Deadline";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} left`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} left`;
    return "Less than 1 hour left";
  };

  const isPastDeadline = (deadline) => new Date(deadline) < new Date();

  const getButtonLabel = (pp) => {
    if (!isProUser) return "Upgrade to Pro";
    if (isPastDeadline(pp.deadline)) return "Expired";
    if (userApplications.has(pp._id)) return "Edit Submission";
    if (pp.maxParticipants && pp.participants?.length >= pp.maxParticipants) return "Full";
    return "Apply Now";
  };

  const handleCtaClick = (pp) => {
    if (!isProUser) {
      setSubscriptionPopupOpen(true);
      return;
    }
    handleApplyClick(pp);
  };

  const handleShare = async (pp) => {
    try {
      await copyShareUrl('powerplay', pp._id);
      notifier.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  return (
    <>
      <div className="powerplays-page">

        <div className="pp-page-header">
          <div className="pp-header-icon">
            <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>
          <h1>PowerPlays</h1>
          <p>Exclusive opportunities for Pro users to grow their following, get featured, and connect with the industry.</p>
        </div>

        {loading ? (
          <div className="pp-loading">
            <CircularProgress size={32} sx={{ color: "#E8D44D" }} />
          </div>

        ) : powerPlays.length === 0 ? (
          <div className="pp-empty">
            <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            <h3>No Active PowerPlays</h3>
            <p>Check back soon for new opportunities!</p>
          </div>

        ) : (
          <div className="pp-cards">
            {powerPlays.map((pp) => (
              <div className="pp-card-wrapper" key={pp._id}>
                <div className="pp-card">
                  {/* Hero */}
                  <div className="pp-card-hero">
                    <div className="pp-scanlines" />

                    {pp.photo && (
                      <img className="pp-hero-photo" src={pp.photo} alt={pp.title} />
                    )}

                    <div className="pp-badge">
                      <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                      <span>PowerPlay</span>
                    </div>

                    <div className="pp-timer-badge">
                      <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" /></svg>
                      <span>{getTimeRemaining(pp.deadline)}</span>
                    </div>

                    <div className="pp-hero-icon">
                      <div className="pp-mic-ring">
                        <svg viewBox="0 0 24 24"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" /></svg>
                      </div>
                      <div className="pp-live-tag">
                        <span className="pp-dot" />
                        Live Stream
                      </div>
                    </div>

                    <Waveform />
                  </div>

                  {/* Content */}
                  <div className="pp-card-content">
                    <h2 className="pp-card-title">
                      <span className="accent">{pp.title.split(' ')[0]}</span>{' '}
                      {pp.title.split(' ').slice(1).join(' ')}
                    </h2>

                    <p className="pp-card-desc">{pp.description}</p>

                    {/* Perks */}
                    <div className="pp-perks">
                      <div className="pp-perk">
                        <div className="pp-perk-icon yellow">
                          <svg viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                        </div>
                        <div className="pp-perk-text">
                          <span className="pp-perk-label">Reward</span>
                          <span className="pp-perk-value">Featured Artist</span>
                        </div>
                      </div>
                      <div className="pp-perk">
                        <div className="pp-perk-icon cyan">
                          <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
                        </div>
                        <div className="pp-perk-text">
                          <span className="pp-perk-label">Access</span>
                          <span className="pp-perk-value">Industry Network</span>
                        </div>
                      </div>
                    </div>

                    {/* Deadline row */}
                    <div className="pp-deadline-row">
                      <svg viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" /></svg>
                      <span className="dl-label">Deadline</span>
                      <span className="dl-value">{formatDate(pp.deadline)}</span>
                    </div>

                    {/* CTA */}
                    <button
                      className={`pp-cta${!isProUser ? ' pp-cta--upgrade' : ''}${userApplications.has(pp._id) ? ' pp-cta--applied' : ''}${isPastDeadline(pp.deadline) ? ' pp-cta--expired' : ''}`}
                      disabled={isProUser && (isPastDeadline(pp.deadline) || (pp.maxParticipants && !userApplications.has(pp._id) && pp.participants?.length >= pp.maxParticipants))}
                      onClick={() => handleCtaClick(pp)}
                    >
                      {!isProUser && (
                        <svg className="pp-cta-lock" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                      )}
                      {getButtonLabel(pp)}
                    </button>
                  </div>

                  {/* Footer */}
                  <div className="pp-card-footer">
                    <span className="pp-footer-tag">Powered by <span className="siki">SIKI</span></span>
                    <button className="pp-share-btn" onClick={() => handleShare(pp)} title="Share this PowerPlay">
                      <svg viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Apply / Edit Dialog */}
      <Dialog
        open={applyDialogOpen}
        onClose={handleCloseApplyDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: "pp-dialog" }}
      >
        {/* Dialog Header */}
        <div className="pp-dialog-header">
          <div className="pp-dialog-badge">
            <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            <span>{isEditing ? "Edit Submission" : "Apply"}</span>
          </div>
          <button className="pp-dialog-close" onClick={handleCloseApplyDialog}>
            <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
          </button>
        </div>

        <DialogContent className="pp-dialog-content">
          <h2 className="pp-dialog-title">{selectedPowerPlay?.title}</h2>
          <p className="pp-dialog-subtitle">
            {isEditing
              ? "Update your application details below."
              : "Tell us why you're a great fit for this opportunity."}
          </p>

          <div className="pp-dialog-form">
            <div className="pp-field">
              <label className="pp-field-label">Your Application</label>
              <textarea
                className="pp-textarea"
                rows={5}
                value={applicationText}
                onChange={(e) => setApplicationText(e.target.value)}
                placeholder="Share your experience, passion, and why you should be selected..."
              />
            </div>

            {selectedPowerPlay?.requiresSongUpload && (
              <div className="pp-field">
                <label className="pp-field-label">Select a Song</label>
                {loadingSongs ? (
                  <div className="pp-select-loading">
                    <CircularProgress size={16} sx={{ color: "#E8D44D" }} />
                    <span>Loading your songs...</span>
                  </div>
                ) : userSongs.length === 0 ? (
                  <p className="pp-select-empty">No songs found. Please upload songs first.</p>
                ) : (
                  <div className="pp-song-grid">
                    {userSongs.map((song) => (
                      <button
                        key={song._id}
                        type="button"
                        className={`pp-song-option${selectedSong === song._id ? ' selected' : ''}`}
                        onClick={() => setSelectedSong(song._id)}
                      >
                        <svg viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" /></svg>
                        <span>{song.title || song.name || "Untitled"}</span>
                        {selectedSong === song._id && (
                          <svg className="pp-song-check" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>

        <div className="pp-dialog-actions">
          <button className="pp-dialog-cancel" onClick={handleCloseApplyDialog}>
            Cancel
          </button>
          <button
            className="pp-dialog-submit"
            disabled={!applicationText.trim() || (selectedPowerPlay?.requiresSongUpload && !selectedSong)}
            onClick={handleSubmitApplication}
          >
            {isEditing ? "Update Submission" : "Submit Application"}
          </button>
        </div>
      </Dialog>

      {/* Subscription Popup — rendered outside powerplays-page to avoid z-index issues */}
      <SubscriptionPopup
        isOpen={subscriptionPopupOpen}
        onClose={() => setSubscriptionPopupOpen(false)}
      />
    </>
  );
}
